import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  Tooltip,
  useTheme,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Download as DownloadIcon,
  WhatsApp,
} from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { LinkedinIcon } from "lucide-react";
import { useTranslation } from "react-i18next"; // Import the translation hook
import useLinkedInPost from "./hooks/LinkedInPost";
import { styled } from "@mui/material/styles";
import colors from "colors/color";
import { NewCreateOfferButton } from "components/Buttons/Buttons";


const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "25px",
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
  },
}));
const QRCodeContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(1),
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.12)",
  margin: "0 auto",
  width: "fit-content",
  maxWidth: "100%",
  transition: "all 0.3s ease",
  [theme.breakpoints.up('xs')]: {
    padding: theme.spacing(1.25),
    transform: "scale(0.95)",
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
    transform: "scale(1)",
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
    transform: "scale(1.05)",
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2.5),
    transform: "scale(1.1)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-3px",
    left: "-3px",
    right: "-3px",
    bottom: "-3px",
    background: "linear-gradient(45deg, #0017E7, #FFCCF8, #0017E7)",
    borderRadius: "23px",
    zIndex: -1,
    animation: "gradientAnimation 3s ease infinite",
    backgroundSize: "200% 200%",
  },
  "@keyframes gradientAnimation": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
}));
const CopyIconButton = styled(IconButton)(({ theme }) => ({
  color: colors.primary,
  backgroundColor: "rgba(0, 23, 231, 0.15)",
  padding: "4px",
  transform: "scale(0.9)",
  transition: "all 0.2s ease",
  flexShrink: 0,
  [theme.breakpoints.up('xs')]: {
    padding: "4px",
    transform: "scale(0.9)",
  },
  [theme.breakpoints.up('sm')]: {
    padding: "5px",
    transform: "scale(1)",
  },
  [theme.breakpoints.up('md')]: {
    padding: "6px",
    transform: "scale(1.1)",
  },
  [theme.breakpoints.up('lg')]: {
    padding: "8px",
    transform: "scale(1.2)",
  },
}));
const LinkContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(0.75),
  backgroundColor: "#EAEAEA",
  borderRadius: "10px",
  marginTop: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.06)",
  border: "1px solid rgba(0, 0, 0, 0.04)",
  transition: "all 0.2s ease",
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  boxSizing: "border-box",
  [theme.breakpoints.up('xs')]: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    gap: theme.spacing(0.75),
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1.5),
    gap: theme.spacing(1),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    gap: theme.spacing(1.25),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2.5),
    gap: theme.spacing(1.5),
  },
}));
const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
  position: 'fixed',
  zIndex: 99999,
  '& .MuiAlert-root': {
    position: 'relative',
    zIndex: 99999,
  }
}));
const QRCodeDialog = ({
  open,
  onClose,
  qrCodeUrl,
  formData,
  callerComponent,
}) => {
  const { t } = useTranslation(); // Initialize the translation hook
  const [urlCopied, setUrlCopied] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const linkedInPost = useLinkedInPost();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');

  const filtredata = (formData) => {
    console.log("datat:", formData);
    const postData = {
      postId: formData.id,
      postImage: formData.image ?? null,
      postTitle: formData.title ?? null,
      postDescription: formData.description ?? null,
      postRequirement: formData.requirements ?? [],
      postDeadline: formData.deadline ?? null,
      location: formData.localisation ?? null,
      workingMode: formData.mode ?? null,
      currency: formData.salary ? "DH" : null, // Example logic for currency
      offerType: formData.offerType ?? null,
    };

    return { postData };
  };
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(qrCodeUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };
  const handleLinkedInRedirect = () => {
    const postData = filtredata(formData);
    console.log("post to share: ", postData);
    linkedInPost.handleLinkedInAuth(postData.postData);
  };
  const handleWhatsAppShare = () => {
    console.log("WhatsApp share clicked");
    const fullMessage = `Hi! I wanted to share this post with you:\n\n${qrCodeUrl}`;
    const canvas = document.getElementById("job-qr-code");
    const qrImageUrl = canvas.toDataURL("image/png");
    const message = encodeURIComponent(fullMessage);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${message}&image=${qrImageUrl}`;
    window.open(whatsappUrl, '_blank');
 
  };

  const handleDownloadQRCode = () => {
    const canvas = document.getElementById("job-qr-code");
    const pngUrl = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "job-qr-code.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const [showCopied, setShowCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrCodeUrl)
      .then(() => {
        setShowCopied(true);
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  };
  const open_popover = Boolean(anchorEl);
  const handleCloseSnackbar = () => {
    setShowCopied(false);
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="qr-code-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <DialogTitle
        id="qr-code-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
          px: 3,
        }}
      >
        <Typography 
        sx={{
          fontSize: 20,
          fontWeight: 500,
          fontFamily: "Poppins",
          color: "#000",
          textTransform:'none'
        }}
        >
          {t('qrCodeDialog.title')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            // mr: 3,
            color: "#000",
            border: '1px solid #D9D9D9',
            "&:hover": {
              color: '#000',
              backgroundColor: '#0084ff08',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
      >
        {qrCodeUrl && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: "white",
              margin: 4,

            }}
          >
            <QRCodeContainer>
              <QRCodeCanvas
                id="job-qr-code"
                value={qrCodeUrl}
                size={180}
                level="H"
                includeMargin={true}
                bgColor={"#FFFFFF"}
                fgColor={colors.primary}
              />
            </QRCodeContainer>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 4 }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: { 
                  xs: "9px",
                  sm: "10px",
                  md: "11px",
                  lg: "12px"
                },
                color: "#666",
                textAlign: "center",
                width: "100%",
                maxWidth: { 
                  xs: "180px",
                  sm: "200px",
                  md: "240px",
                  lg: "280px"
                },
                fontWeight: 500,
                lineHeight: 1.3,
                px: { 
                  xs: 0.5,
                  sm: 0.75,
                  md: 1,
                  lg: 1.25
                },
                boxSizing: "border-box",
              }}
            >
              {t('qrCodeDialog.scanInstructions')}
            </Typography>
            </Box>
            <Box >
            <LinkContainer>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: { 
                    xs: "8px",
                    sm: "9px",
                    md: "10px",
                    lg: "11px"
                  },
                  color: "#333",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                  minWidth: 0,
                }}
              >
                {qrCodeUrl}
              </Typography>
              <CopyIconButton onClick={handleCopyLink}>
                <ContentCopyIcon fontSize={isMobile ? "small" : "medium"} />
              </CopyIconButton>
              <CopyIconButton
                onClick={() => window.open(qrCodeUrl, "_blank", "noopener,noreferrer")}
                sx={{ ml: 0.5 }}
                title={t("openInNewTab") || "Open in new tab"}
              >
                <svg
                  width={isMobile ? 18 : 20}
                  height={isMobile ? 18 : 20}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: "block" }}
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </CopyIconButton>
            </LinkContainer>
            </Box>
            
            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 4 }}>
              <Typography
                variant="subtitle1"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
              >
                {qrCodeUrl}
              </Typography>
              <Tooltip
                title={urlCopied ? t("copied") : t("copyUrl")}
                placement="top"
                arrow
              >
                <IconButton
                  onClick={handleCopyUrl}
                  color={urlCopied ? "success" : "default"}
                  size="small"
                  sx={{
                    transition: "all 0.3s",
                    "&:hover": {
                      backgroundColor: urlCopied
                        ? theme.palette.success.light
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  {urlCopied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box> */}
            <Popper
              open={open_popover}
              anchorEl={anchorEl}
              placement="top"
              transition
            >
              {({ TransitionProps }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: "center top",
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handlePopoverClose}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          p: 2,
                          maxWidth: "400px",
                          wordBreak: "break-all",
                          textAlign: "center",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {qrCodeUrl}
                      </Typography>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            <StyledSnackbar
    open={showCopied}
    autoHideDuration={3000}
    onClose={handleCloseSnackbar}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert
      onClose={handleCloseSnackbar}
      severity="success"
      variant="filled"
      sx={{
        width: "100%",
        fontFamily: "Poppins",
        backgroundColor: colors.primary,
        borderRadius: "12px",
        boxShadow: "0 6px 20px rgba(0, 23, 231, 0.25)",
        "& .MuiAlert-icon": {
          marginRight: 1,
        },
        fontSize: "15px",
        fontWeight: 500,
        paddingY: 1.2,
        paddingX: 2,
        position: 'relative',
        zIndex: 99999,
      }}
    >
      {t('qrCodeDialog.linkCopied')}
    </Alert>
  </StyledSnackbar>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          py: 2,
          px: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >

        <NewCreateOfferButton
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadQRCode}
          sx={{
            bgcolor: '#e5e8e8', 
            color: colors.primary,
            boxShadow:'none',
            border: `1px solid ${colors.primary}` ,
            textTransform: 'none',
            '&:hover': {
              fontWeight:400,
                       },
          }}
        >
          {t('qrCodeDialog.downloadQR')}
        </NewCreateOfferButton>
        {/* {callerComponent !== 'ApplicationFilters' && (
          <Button
            onClick={handleLinkedInRedirect}
            variant="contained"
            color="primary"
            startIcon={<LinkedinIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: 2,
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              }
            }}
          >
            {t('linkedIn')}
          </Button>
        )} */}

        <NewCreateOfferButton
          variant="contained"
          startIcon={<LinkedinIcon />}
          onClick={handleLinkedInRedirect}
          sx={{
            bgcolor: '#0077B5', // Official LinkedIn Blue
            color: '#fff',
            boxShadow:'none',
            border: `none` ,
            // minWidth: "140px",
            // minHeight: "40px",
            '&:hover': {
              fontWeight:400,
                       },
            textTransform: 'none',
          }}
        >
          {t('qrCodeDialog.linkedin')}
        </NewCreateOfferButton>
        <NewCreateOfferButton
          variant="contained"
          startIcon={<WhatsApp />}
          onClick={handleWhatsAppShare}
          sx={{
            bgcolor: '#25D366', // WhatsApp green
            color: '#fff',
            minWidth: "140px",
            minHeight: "40px",
            boxShadow:'none',
            border: `none` ,
            '&:hover': {
              fontWeight:400,
            },
            textTransform: 'none',
          }}
        >
          {t('qrCodeDialog.whatsapp')}
        </NewCreateOfferButton>
        {/* <Button
          onClick={handleLinkedInRedirect}
          variant="contained"
          color="primary"
          startIcon={<LinkedinIcon />}
          sx={{
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: 2,
            transition: "all 0.3s",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[4],
            },
          }}
        >
          {t("linkedIn")}
        </Button> */}
      </DialogActions>
    </Dialog>
    
  );
};

export default QRCodeDialog;
